import { RequestFailed, readJson, request } from './request'

/**
 * Everything the frontend needs to know about signing in — asked of the service, never
 * decided here.
 *
 * The ways in are a list the service sends, not a button written into this file. That is
 * what makes a second identity provider a matter of configuration: it appears here on its
 * own, with its own name and its own address.
 */

/** One way into MyLib, as the service offers it. */
export interface SignInMethod {
  readonly id: string
  readonly name: string
  readonly url: string
}

/** The token a request that changes something has to carry, and the header it belongs in. */
export interface Csrf {
  readonly token: string
  readonly headerName: string
}

export interface Session {
  readonly signedIn: boolean
  /** The name the provider knows the signed-in person by, or `null` when nobody is. */
  readonly userName: string | null
  readonly signInMethods: readonly SignInMethod[]
  readonly signOutUrl: string
  /** Whom to ask when the answer was no. */
  readonly contact: string
  readonly csrf: Csrf
}

const SESSION_ENDPOINT = '/api/session'

/**
 * Asks the service who is signed in.
 *
 * Answered without a session as well — that is the point of it. Fails only when the service itself
 * cannot be reached, and then with a {@link import('./request').Failure} fit to show a user.
 */
export async function fetchSession(signal?: AbortSignal): Promise<Session> {
  const response = await request(SESSION_ENDPOINT, { signal })
  const body = await readJson<SessionPayload>(response)

  return {
    signedIn: body.signedIn === true,
    userName: typeof body.user?.name === 'string' ? body.user.name : null,
    signInMethods: body.signInMethods ?? [],
    signOutUrl: body.signOutUrl ?? '',
    contact: body.contact ?? '',
    csrf: body.csrf ?? { token: '', headerName: 'X-XSRF-TOKEN' },
  }
}

/** The shape on the wire, which nests the user; the frontend only ever needs the name. */
interface SessionPayload {
  readonly signedIn?: boolean
  readonly user?: { readonly name?: string } | null
  readonly signInMethods?: readonly SignInMethod[]
  readonly signOutUrl?: string
  readonly contact?: string
  readonly csrf?: Csrf
}

/**
 * Ends the session deliberately.
 *
 * Deliberate is the only way it ever ends: the service keeps it for weeks otherwise. The
 * token proves the request came from this page and not from some other site.
 */
export async function signOut(session: Session): Promise<void> {
  try {
    await request(session.signOutUrl, {
      method: 'POST',
      headers: { [session.csrf.headerName]: session.csrf.token },
    })
  } catch (cause) {
    if (cause instanceof RequestFailed) {
      throw new RequestFailed(
        {
          ...cause.failure,
          message: 'Signing out did not work.',
          retryable: true,
        },
        { cause },
      )
    }
    throw cause
  }
}
